/**
 * MIQ Score Curve Utility
 * 
 * Implements the deterministic 30-day calibration curve for MIQ Score.
 * 
 * IMPORTANT: This is a user-facing motivational score (0-100 scale),
 * NOT the backend model confidence percentage.
 * 
 * Anchor Points (Base Score without Momentum):
 * - Day 1:  15
 * - Day 7:  22
 * - Day 14: 26
 * - Day 20: 30
 * - Day 27: 37
 * - Day 30: 42
 * 
 * Momentum Bonus Rule:
 * - When baseScore >= 25, add +10 "Momentum Bonus"
 * - This activates around Day 14
 * 
 * With Momentum Applied:
 * - Day 14: 36
 * - Day 20: 40
 * - Day 27: 47
 * - Day 30: 52 → Baseline unlocks
 * 
 * Formula: DisplayedMIQ = baseScore + (momentumActive ? 10 : 0)
 */

// Anchor points for the base curve (without momentum)
const ANCHOR_POINTS = [
  { day: 1, score: 15 },
  { day: 7, score: 22 },
  { day: 14, score: 26 },
  { day: 20, score: 30 },
  { day: 27, score: 37 },
  { day: 30, score: 42 }
];

// Momentum bonus threshold and value
const MOMENTUM_THRESHOLD = 25;
const MOMENTUM_BONUS = 10;

/**
 * Linear interpolation between two points
 */
const lerp = (x, x1, y1, x2, y2) => {
  if (x2 === x1) return y1;
  return y1 + (y2 - y1) * ((x - x1) / (x2 - x1));
};

/**
 * Calculate base MIQ score for a given calibration day
 * Uses linear interpolation between anchor points
 * 
 * @param {number} day - Current calibration day (1-30+)
 * @returns {number} Base MIQ score (without momentum)
 */
export const getBaseMIQScore = (day) => {
  // Clamp day to valid range
  const clampedDay = Math.max(1, Math.min(day, 30));
  
  // Find surrounding anchor points
  let lowerAnchor = ANCHOR_POINTS[0];
  let upperAnchor = ANCHOR_POINTS[ANCHOR_POINTS.length - 1];
  
  for (let i = 0; i < ANCHOR_POINTS.length - 1; i++) {
    if (clampedDay >= ANCHOR_POINTS[i].day && clampedDay <= ANCHOR_POINTS[i + 1].day) {
      lowerAnchor = ANCHOR_POINTS[i];
      upperAnchor = ANCHOR_POINTS[i + 1];
      break;
    }
  }
  
  // Interpolate between anchor points
  const baseScore = lerp(
    clampedDay,
    lowerAnchor.day,
    lowerAnchor.score,
    upperAnchor.day,
    upperAnchor.score
  );
  
  return Math.round(baseScore);
};

/**
 * Check if momentum bonus is active
 * Momentum activates when base score >= 25 (around Day 14)
 * 
 * @param {number} baseScore - Base MIQ score
 * @returns {boolean} Whether momentum bonus is active
 */
export const isMomentumActive = (baseScore) => {
  return baseScore >= MOMENTUM_THRESHOLD;
};

/**
 * Calculate the displayed MIQ score with momentum bonus applied
 * 
 * @param {number} day - Current calibration day (1-30+)
 * @returns {{ displayedScore: number, baseScore: number, momentumActive: boolean }}
 */
export const getDisplayedMIQScore = (day) => {
  const baseScore = getBaseMIQScore(day);
  const momentumActive = isMomentumActive(baseScore);
  const displayedScore = baseScore + (momentumActive ? MOMENTUM_BONUS : 0);
  
  return {
    displayedScore,
    baseScore,
    momentumActive,
    momentumBonus: momentumActive ? MOMENTUM_BONUS : 0
  };
};

/**
 * Get MIQ status label based on displayed score
 * 
 * @param {number} displayedScore - The displayed MIQ score (with momentum)
 * @param {boolean} isCalibrating - Whether still in calibration period
 * @returns {string} Status label
 */
export const getMIQStatusLabel = (displayedScore, isCalibrating) => {
  if (isCalibrating) {
    return 'Calibrating';
  }
  if (displayedScore >= 70) return 'Excellent';
  if (displayedScore >= 50) return 'Established';
  if (displayedScore >= 30) return 'Building';
  return 'Learning';
};

/**
 * Get MIQ status color based on displayed score
 * 
 * @param {number} displayedScore - The displayed MIQ score
 * @param {boolean} isCalibrating - Whether still in calibration period
 * @returns {string} CSS color value
 */
export const getMIQStatusColor = (displayedScore, isCalibrating) => {
  if (isCalibrating) return '#ff9800'; // Orange for calibrating
  if (displayedScore >= 70) return '#38ef7d'; // Green
  if (displayedScore >= 50) return '#667eea'; // Purple
  if (displayedScore >= 30) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

/**
 * Get expected scores for documentation/testing
 */
export const getExpectedScores = () => ({
  day1: getDisplayedMIQScore(1),   // { displayedScore: 15, baseScore: 15, momentumActive: false }
  day7: getDisplayedMIQScore(7),   // { displayedScore: 22, baseScore: 22, momentumActive: false }
  day14: getDisplayedMIQScore(14), // { displayedScore: 36, baseScore: 26, momentumActive: true }
  day20: getDisplayedMIQScore(20), // { displayedScore: 40, baseScore: 30, momentumActive: true }
  day27: getDisplayedMIQScore(27), // { displayedScore: 47, baseScore: 37, momentumActive: true }
  day30: getDisplayedMIQScore(30)  // { displayedScore: 52, baseScore: 42, momentumActive: true }
});

export default {
  getBaseMIQScore,
  getDisplayedMIQScore,
  isMomentumActive,
  getMIQStatusLabel,
  getMIQStatusColor,
  getExpectedScores,
  MOMENTUM_THRESHOLD,
  MOMENTUM_BONUS
};
